import type { ReactNode } from 'react';
import React, { useCallback } from 'react';


import { Button } from '@twilio-paste/core/button';
import { Form } from '@twilio-paste/core/form';
import type { PopoverStateReturnProps } from '@twilio-paste/core/popover';
import { Formik } from 'formik';
import type { FormikHelpers, FormikProps, FormikConfig, FormikValues } from 'formik';

import { Drawer } from '../Drawer';

import { FormDrawerErrorState } from './FormDrawerErrorState';
import { FormDrawerLoadingState } from './FormDrawerLoadingState';

export type DrawerDisplayProps = {
  state: PopoverStateReturnProps;
  modal?: boolean;
  onClose?: () => void;
};

interface OwnProps<T> {
  title: string;
  subtitle?: string;
  cancelLabel?: string;
  submitLabel?: string;
  submittingLabel?: string;
  isError?: boolean;
  isLoading?: boolean;
  children: (form: FormikProps<T>) => NonNullable<ReactNode>;
  handleSubmit: (data: T, formActions: FormikHelpers<T>) => void | Promise<void>;
  handleSubmitError?: (error: unknown) => void;
  handleAfterSubmit?: VoidFunction;
}

type FormikFormProps<T> = Pick<
  FormikConfig<T>,
  | 'initialValues'
  | 'validationSchema'
  | 'enableReinitialize'
  | 'validateOnBlur'
  | 'validateOnChange'
>;

export type FormDrawerProps<T> = DrawerDisplayProps & FormikFormProps<T> & OwnProps<T>;

export function FormDrawer<T extends FormikValues>({
  modal,
  title,
  subtitle,
  ...rest
}: FormDrawerProps<T>): JSX.Element {
  return (
    <Drawer aria-label={title} state={rest.state} modal={modal}>
      <Drawer.Header title={title} subtitle={subtitle} />

      <FormDrawerContent {...rest} />
    </Drawer>
  );
}

type FormDrawerContentProps<T extends FormikValues> = Pick<
  FormDrawerProps<T>,
  | 'cancelLabel'
  | 'handleSubmit'
  | 'state'
  | 'handleSubmitError'
  | 'handleAfterSubmit'
  | 'children'
  | 'submitLabel'
  | 'isError'
  | 'isLoading'
> &
  FormikFormProps<T>;

function FormDrawerContent<T extends FormikValues>({
  children,
  handleSubmit,
  cancelLabel = 'Cancel',
  submitLabel = 'Save Changes',
  handleSubmitError,
  handleAfterSubmit,
  isError,
  isLoading,
  state,
  ...rest
}: FormDrawerContentProps<T>) {
  const handleDismiss = useCallback(() => {
    state.hide();
  }, [state]);

  const handleFormSubmit = async (data: T, formikActions: FormikHelpers<T>) => {
    try {
      await handleSubmit(data, formikActions);
      handleDismiss();
      handleAfterSubmit?.();
    } catch (error) {
      handleSubmitError?.(error);
    } finally {
      formikActions.setSubmitting(false);
    }
  };

  if (isLoading) {
    return <FormDrawerLoadingState />;
  }

  if (isError) {
    return <FormDrawerErrorState />;
  }

  return (
    <Formik {...rest} onSubmit={handleFormSubmit}>
      {form => (
        <>
          <Drawer.Content>
            <Form onSubmit={form.handleSubmit} onReset={form.handleReset}>
              {children(form)}
            </Form>
          </Drawer.Content>
          <Drawer.Footer>
            <Button variant="secondary" onClick={handleDismiss}>
              {cancelLabel}
            </Button>
            <Button
              variant="primary"
              onClick={form.submitForm}
              loading={form.isSubmitting}
              disabled={!form.dirty || !form.isValid}
            >
              {submitLabel}
            </Button>
          </Drawer.Footer>
        </>
      )}
    </Formik>
  );
}
